/**
 * Verifica si un usuario tiene un permiso específico en un módulo.
 * Función pura: no depende de hooks ni de estado global.
 */
export function checkUserPermission(
  user: any,
  moduleName: string,
  permissionName: string
): boolean {
  if (!user?.roles) return false;

  const checkModule = (module: any, targetName: string): boolean => {
    if (module.nombre === targetName) {
      return module.permisos?.some((p: any) => p.nombre === permissionName) ?? false;
    }
    if (module.submodulos && Array.isArray(module.submodulos)) {
      return module.submodulos.some((sub: any) => checkModule(sub, targetName));
    }
    return false;
  };

  // Alias de compatibilidad (igual que en usePermissions.ts)
  const modulesToCheck =
    moduleName === 'Administradores'
      ? ['Administradores', 'Usuarios']
      : moduleName === 'Clientes'
      ? ['Clientes', 'Membresías']
      : [moduleName];

  return user.roles.some((role: any) =>
    role.modulos?.some((mod: any) =>
      modulesToCheck.some((name) => checkModule(mod, name))
    )
  );
}

/** Mapeo de nombres de módulo a rutas del dashboard */
const MODULE_ROUTE_MAP: Record<string, string> = {
  Dashboard: '/dashboard',
  Productos: '/dashboard/productos',
  Ventas: '/dashboard/ventas',
  Clientes: '/dashboard/clientes',
  Administradores: '/dashboard/administradores',
  Proveedores: '/dashboard/proveedores',
  'Punto de Venta': '/dashboard/pos',
  'Cierre de Caja': '/dashboard/cierre-caja',
  Reportes: '/dashboard/reportes',
  Roles: '/dashboard/seguridad/roles',
  Módulos: '/dashboard/seguridad/modulos',
  Permisos: '/dashboard/seguridad/permisos',
};

/**
 * Devuelve la primera ruta accesible para el usuario.
 *
 * Prioridad:
 * 1. Dashboard (si tiene permiso)
 * 2. Productos (default para usuarios sin Dashboard)
 * 3. Primer módulo accesible del mapa
 * 4. /dashboard como fallback absoluto
 */
export function getFirstAccessibleRoute(user: any): string {
  if (checkUserPermission(user, 'Dashboard', 'ver')) {
    return '/dashboard';
  }

  if (checkUserPermission(user, 'Productos', 'ver')) {
    return '/dashboard/productos';
  }

  for (const [moduleName, route] of Object.entries(MODULE_ROUTE_MAP)) {
    if (moduleName === 'Dashboard' || moduleName === 'Productos') continue;
    if (checkUserPermission(user, moduleName, 'ver')) {
      return route;
    }
  }

  return '/dashboard';
}
